import type { BusinessWorkspace } from './types';

export interface ProfitabilityResult {
  totalCost: number;
  profit: number;
  marginRate: number;
  costRate: number;
}

export interface PricingResult {
  variableCostPerUnit: number;
  contributionPerUnit: number;
  currentMarginRate: number;
  targetPrice: number | null;
  discount10ProfitPerUnit: number;
}

export interface PlanningResult {
  contributionPerUnit: number;
  breakEvenUnits: number | null;
  breakEvenRevenue: number | null;
  monthlyRevenue: number;
  monthlyProfit: number;
  targetProfitUnits: number | null;
  runwayMonths: number | null;
}

export interface GrowthResult {
  roas: number | null;
  contributionBeforeAds: number;
  estimatedProfitAfterAds: number;
  breakEvenRoas: number | null;
  maxAdSpend: number;
  targetRevenue: number | null;
}

function safeRate(numerator: number, denominator: number): number {
  return denominator > 0 ? (numerator / denominator) * 100 : 0;
}

export function calculateProfitability(workspace: BusinessWorkspace): ProfitabilityResult {
  const totalCost = workspace.directCost + workspace.variableCost + workspace.fixedCost + workspace.adSpend;
  const profit = workspace.revenue - totalCost;
  return {
    totalCost,
    profit,
    marginRate: safeRate(profit, workspace.revenue),
    costRate: safeRate(totalCost, workspace.revenue),
  };
}

export function calculatePricing(workspace: BusinessWorkspace): PricingResult {
  const feePerUnit = workspace.unitPrice * (workspace.feeRate / 100);
  const variableCostPerUnit = workspace.unitCost + workspace.unitOtherCost + feePerUnit;
  const contributionPerUnit = workspace.unitPrice - variableCostPerUnit;
  const currentMarginRate = safeRate(contributionPerUnit, workspace.unitPrice);
  const denominator = 1 - (workspace.feeRate + workspace.targetMarginRate) / 100;
  const targetPrice = denominator > 0 ? (workspace.unitCost + workspace.unitOtherCost) / denominator : null;
  const discountedPrice = workspace.unitPrice * 0.9;
  const discountedFee = discountedPrice * (workspace.feeRate / 100);
  const discount10ProfitPerUnit = discountedPrice - workspace.unitCost - workspace.unitOtherCost - discountedFee;

  return {
    variableCostPerUnit,
    contributionPerUnit,
    currentMarginRate,
    targetPrice,
    discount10ProfitPerUnit,
  };
}

export function calculatePlanning(workspace: BusinessWorkspace): PlanningResult {
  const feePerUnit = workspace.unitPrice * (workspace.feeRate / 100);
  const contributionPerUnit = workspace.unitPrice - workspace.unitCost - workspace.unitOtherCost - feePerUnit;
  const breakEvenUnits = contributionPerUnit > 0 ? Math.ceil(workspace.fixedCost / contributionPerUnit) : null;
  const breakEvenRevenue = breakEvenUnits === null ? null : breakEvenUnits * workspace.unitPrice;
  const monthlyRevenue = workspace.unitPrice * workspace.units;
  const monthlyProfit = contributionPerUnit * workspace.units - workspace.fixedCost;
  const targetProfitUnits = contributionPerUnit > 0
    ? Math.ceil((workspace.fixedCost + workspace.targetProfit) / contributionPerUnit)
    : null;
  const runwayMonths = monthlyProfit < 0 && workspace.cashBalance > 0
    ? workspace.cashBalance / Math.abs(monthlyProfit)
    : null;

  return {
    contributionPerUnit,
    breakEvenUnits,
    breakEvenRevenue,
    monthlyRevenue,
    monthlyProfit,
    targetProfitUnits,
    runwayMonths,
  };
}

export function calculateGrowth(workspace: BusinessWorkspace): GrowthResult {
  const contributionBeforeAds = workspace.attributedRevenue * (workspace.grossMarginRate / 100);
  const estimatedProfitAfterAds = contributionBeforeAds - workspace.adSpend;
  const roas = workspace.adSpend > 0 ? (workspace.attributedRevenue / workspace.adSpend) * 100 : null;
  const breakEvenRoas = workspace.grossMarginRate > 0 ? 10000 / workspace.grossMarginRate : null;
  const maxAdSpend = contributionBeforeAds;
  const targetRevenue = workspace.grossMarginRate > 0
    ? (workspace.adSpend + workspace.targetProfit) / (workspace.grossMarginRate / 100)
    : null;

  return {
    roas,
    contributionBeforeAds,
    estimatedProfitAfterAds,
    breakEvenRoas,
    maxAdSpend,
    targetRevenue,
  };
}
